import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { AwsSdkService } from 'src/app/shared/services/aws-sdk.service';
import { WebApiService } from 'src/app/shared/services/web-api.service';
import { CookieService } from 'ngx-cookie-service';
import { FullLoaderService } from 'src/app/shared/services/full-loader.service';
import { SupportTicketSystemService } from '../support-ticket-system.service';
import {
  EmailPreviewData,
  SupportTicket,
  SupportTicketPriority,
  SupportTicketStatus,
  TicketFormValues
} from '../support-ticket-system.models';

@Component({
  selector: 'app-supplier-support-ticket-system',
  templateUrl: './supplier-support-ticket-system.component.html',
  styleUrls: ['./supplier-support-ticket-system.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class SupplierSupportTicketSystemComponent implements OnInit {
  private readonly defaultDisplayName = 'Supplier User';
  private readonly defaultAccountLabel = 'Supplier Account';
  private readonly defaultEmail = 'supplier@example.com';

  displayName = this.defaultDisplayName;
  accountLabel = this.defaultAccountLabel;
  allowTicketView = true;
  emailEntry = '';
  supplierName = '';
  supplierId: number | null = null;
  supplierAccountName = '';
  supplierAccountEmails: string[] = [];

  stats = { total: 0, open: 0, inProgress: 0, resolved: 0 };
  filterStatus = 'all';
  searchTerm = '';
  showCreateModal = false;
  showDetailsModal = false;
  showEmailModal = false;
  showStatusModal = false;
  dragActive = false;

  tickets: SupportTicket[] = [];
  selectedTicket: SupportTicket | null = null;
  emailPreview!: EmailPreviewData;
  uploadedFiles: { file: File; name: string; size: number; displaySize: string }[] = [];
  newTicket!: TicketFormValues;
  statusUpdateOptions: string[] = [];
  pendingStatusTicket: SupportTicket | null = null;
  statusUpdateSelection: SupportTicketStatus | '' = '';

  constructor(
    private awsSdkService: AwsSdkService,
    private webApiService: WebApiService,
    private cookieService: CookieService,
    private fullLoaderService: FullLoaderService,
    private supportTicketService: SupportTicketSystemService
  ) {
    const idFromCookie = this.cookieService.get('userId');
    this.supplierId = idFromCookie ? Number(idFromCookie) : null;
    this.newTicket = this.buildNewTicket();
    this.emailEntry = this.newTicket.emails.join(', ');
  }

  ngOnInit(): void {
    this.loadSupplierTickets();
  }

  get userInitials(): string {
    return this.displayName
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  }

  get filteredTickets(): SupportTicket[] {
    if (!this.searchTerm) return this.tickets;
    const term = this.searchTerm.toLowerCase();
    return this.tickets.filter((t) => {
      const matchesId = (t.ticketRefNumber || t.id || '').toLowerCase().includes(term);
      const matchesCategory = (t.category || '').toLowerCase().includes(term);
      const matchesBuyer = (t.buyerName || '').toLowerCase().includes(term);
      const matchesPriority = (t.priority || '').toLowerCase().includes(term);
      return matchesId || matchesCategory || matchesBuyer || matchesPriority;
    });
  }

  getStatusClass(status: SupportTicketStatus): string {
    switch (status) {
      case 'Open':
        return 'status-open';
      case 'In Progress':
        return 'status-in-progress';
      case 'Resolved':
        return 'status-resolved';
      case 'Closed':
        return 'status-closed';
      default:
        return '';
    }
  }

  getPriorityClass(priority: SupportTicketPriority): string {
    switch (priority) {
      case 'High':
        return 'priority-high';
      case 'Medium':
        return 'priority-medium';
      case 'Low':
        return 'priority-low';
      case 'Urgent':
        return 'priority-urgent';
      default:
        return '';
    }
  }

  openCreateTicket(): void {
    this.newTicket = this.buildNewTicket();
    this.emailEntry = this.newTicket.emails.join(', ');
    this.supplierName = this.supplierAccountName || this.supplierName || this.displayName;
    this.uploadedFiles = [];
    this.showCreateModal = true;
  }

  closeModals(): void {
    this.showCreateModal = false;
    this.showDetailsModal = false;
    this.showEmailModal = false;
    this.showStatusModal = false;
    this.pendingStatusTicket = null;
    this.statusUpdateSelection = '';
  }

  printEmailPreview(): void {
    const preview = document.getElementById('supplierEmailPreview');
    if (!preview) {
      console.warn('Email preview not found for printing.');
      return;
    }

    const ticketRef = this.selectedTicket?.ticketRefNumber || this.selectedTicket?.id || 'Ticket';
    const title = `Supplier_Support_Ticket_System_${ticketRef}.pdf`;
    const printMarkup = `
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #1f2d3d; }
            .email-preview { max-width: 760px; margin: 0 auto; }
            .email-header { margin-bottom: 16px; }
            .email-to { margin-bottom: 6px; font-size: 14px; }
            .email-body h4 { margin: 16px 0 8px; }
            .email-body p, .email-body li { font-size: 14px; line-height: 1.5; }
            .email-body ul, .email-body ol { padding-left: 20px; }
          </style>
        </head>
        <body>
          ${preview.innerHTML}
        </body>
      </html>
    `;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const frameDoc = iframe.contentWindow?.document;
    if (!frameDoc || !iframe.contentWindow) {
      console.warn('Unable to access print frame.');
      document.body.removeChild(iframe);
      return;
    }

    frameDoc.open();
    frameDoc.write(printMarkup);
    frameDoc.close();

    iframe.contentWindow.focus();
    iframe.contentWindow.print();

    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 500);
  }

  setFilter(filter: string): void {
    this.filterStatus = filter;
    const status = this.mapFilterToStatus(filter);
    this.loadSupplierTickets(status);
  }

  openTicketDetails(ticket: SupportTicket): void {
    this.selectedTicket = ticket;
    this.showDetailsModal = true;
  }

  openTicketEmail(ticket: SupportTicket): void {
    this.emailPreview = this.buildEmailPreviewFromTicket(ticket);
    this.showEmailModal = true;
  }

  openStatusModal(ticket: SupportTicket): void {
    this.pendingStatusTicket = ticket;
    this.statusUpdateOptions = this.getStatusUpdateOptions(ticket.status);
    this.statusUpdateSelection = (this.statusUpdateOptions[0] as SupportTicketStatus) || '';
    this.showStatusModal = true;
  }

  async submitStatusUpdate(newStatus: string | SupportTicketStatus | ''): Promise<void> {
    const resolvedStatus = newStatus || this.statusUpdateSelection;
    const nextStatus = this.validateStatus(resolvedStatus);
    if (!this.pendingStatusTicket || !nextStatus) {
      this.showStatusModal = false;
      this.pendingStatusTicket = null;
      this.statusUpdateSelection = '';
      return;
    }
    this.fullLoaderService.showLoader('Updating ticket status...');
    try {
      await this.supportTicketService.updateTicketStatusBySupplier({
        ticketId: (this.pendingStatusTicket.ticketId ?? this.pendingStatusTicket.id) as string,
        status: nextStatus
      });
      this.showStatusModal = false;
      this.pendingStatusTicket = null;
      this.statusUpdateSelection = '';
      await this.loadSupplierTickets(this.mapFilterToStatus(this.filterStatus));
    } catch (err) {
      console.error('Failed to update supplier ticket status', err);
    } finally {
      this.fullLoaderService.hideLoader();
    }
  }

  async downloadTicketDocuments(ticket?: SupportTicket | string): Promise<void> {
    const targetTicket =
      typeof ticket === 'string'
        ? this.tickets.find((t) => t.id === ticket) || this.selectedTicket
        : ticket || this.selectedTicket;

    if (!targetTicket || !targetTicket.attachments?.length) {
      console.warn('No attachments available to download for this ticket.');
      return;
    }

    this.fullLoaderService.showLoader('Downloading attachments...');
    try {
      for (const attachment of targetTicket.attachments) {
        // console.log('Attempting bulk download of attachment', attachment?.name, 'with link', attachment?.link);
        await this.triggerAttachmentDownload(attachment);
      }
    } finally {
      this.fullLoaderService.hideLoader();
    }
  }

  async downloadAttachment(name: string): Promise<void> {
    const ticket = this.selectedTicket;
    if (!ticket || !ticket.attachments?.length) {
      console.warn('No attachments available to download.');
      return;
    }
    const attachment = ticket.attachments.find((a) => a.name === name) || ticket.attachments[0];
    await this.triggerAttachmentDownload(attachment);
  }

  triggerFileBrowse(input: HTMLInputElement): void {
    input.click();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragActive = true;
  }

  onDragLeave(): void {
    this.dragActive = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragActive = false;
    if (event.dataTransfer?.files) {
      this.handleFiles(event.dataTransfer.files);
    }
  }

  onFileChange(event: Event): void {
    const files = (event.target as HTMLInputElement).files;
    this.handleFiles(files);
    (event.target as HTMLInputElement).value = '';
  }

  removeFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
  }

  async submitTicket(): Promise<void> {
    this.syncEmails();
    const missing: string[] = [];
    if (!this.supplierName.trim()) missing.push('Supplier Name');
    if (!this.newTicket.emails.length) missing.push('Email(s)');
    if (!this.newTicket.subject) missing.push('Subject');
    if (!this.newTicket.category) missing.push('Category');
    if (!this.newTicket.description) missing.push('Description');

    if (missing.length) {
      console.warn('Missing fields:', missing.join(', '));
      return;
    }

    this.fullLoaderService.showLoader('Submitting support ticket...');
    try {
      const documentDetails = await this.uploadAttachmentsToS3();

      const backendPayload = {
        supplierId: this.supplierId,
        supplierName: this.supplierAccountName || this.supplierName,
        emails: this.newTicket.emails,
        subject: this.newTicket.subject,
        category: this.newTicket.category,
        priority: this.newTicket.priority,
        description: this.newTicket.description,
        attachments: documentDetails.map((doc) => ({
          documentName: doc.documentName,
          documentType: doc.documentType,
          documentFileS3Path: doc.s3path
        }))
      };

      const response = await this.supportTicketService.createSupplierTicket(backendPayload);
      const respTicket = response?.ticket || response || null;
      const respAttachments = response?.attachments || [];

      const ticket: SupportTicket = {
        id: respTicket?.ticketRefNumber || respTicket?.id || this.generateTicketId(),
        ticketRefNumber: respTicket?.ticketRefNumber || respTicket?.id,
        subject: this.newTicket.subject,
        category: this.getCategoryLabel(this.newTicket.category),
        status: 'Open',
        priority: this.formatPriority(this.newTicket.priority),
        created: new Date().toISOString().split('T')[0],
        submitter: `${this.supplierAccountName || this.supplierName || this.displayName} (${this.newTicket.emails.join(', ')}) - ${this.accountLabel}`,
        buyerName: this.supplierAccountName || this.supplierName,
        emails: [...this.newTicket.emails],
        contactEmail: this.newTicket.emails.join(', '),
        description: this.newTicket.description,
        attachments: (respAttachments.length ? respAttachments : documentDetails).map((doc: any) => ({
          name: doc.documentName || doc.name,
          size: '',
          icon: 'fas fa-file',
          link: doc.documentFileS3Path || doc.s3path || doc.url || ''
        }))
      };

      this.tickets = [ticket, ...this.tickets];
      this.selectedTicket = ticket;
      this.emailPreview = this.buildEmailPreviewFromTicket(ticket, this.newTicket.emails[0]);

      this.showCreateModal = false;
      this.showEmailModal = true;
      this.uploadedFiles = [];
      this.newTicket = this.buildNewTicket();
      this.emailEntry = this.newTicket.emails.join(', ');
      this.supplierName = this.supplierAccountName || this.supplierName || this.displayName;
      await this.loadSupplierTickets(this.mapFilterToStatus(this.filterStatus));
    } catch (err) {
      console.error('Failed to submit support ticket', err);
    } finally {
      this.fullLoaderService.hideLoader();
    }
  }

  async exportTickets(): Promise<void> {
    try {
      this.fullLoaderService.showLoader('Exporting tickets...');
      const XLSX = await import('xlsx');

      const exportData = this.tickets.map((ticket, index) => {
        const attachmentsLinks = (ticket.attachments || [])
          .map((a) => a.link || a.name)
          .filter(Boolean)
          .join(', ');

        return {
          'Sr.No': index + 1,
          'Ticket Ref Number': ticket.id,
          Buyer_Name: ticket.buyerName || '',
          Emails: (ticket.emails && ticket.emails.length ? ticket.emails : ticket.contactEmail ? [ticket.contactEmail] : []).join(', '),
          Subject: ticket.subject || '',
          Description: ticket.description || '',
          Category: ticket.category || '',
          Status: ticket.status || '',
          Priority: ticket.priority || '',
          Attachments_Links: attachmentsLinks,
          Created_On: ticket.created || ''
        };
      });

      const statusLabel = this.mapFilterToStatus(this.filterStatus) || 'All_Tickets';
      const today = new Date();
      const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const fileName = `Support_Ticket_System_${statusLabel.replace(/\s+/g, '_')}_${formattedDate}.xlsx`;

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Tickets');
      XLSX.writeFile(wb, fileName);
    } catch (err) {
      console.error('Failed to export tickets', err);
    } finally {
      this.fullLoaderService.hideLoader();
    }
  }

  syncEmails(): void {
    const emails = this.emailEntry
      .split(',')
      .map((e) => e.trim())
      .filter((e) => !!e);
    this.newTicket.emails = emails.length ? emails : this.newTicket.emails;
  }

  private async loadSupplierTickets(statusFilter?: string): Promise<void> {
    this.fullLoaderService.showLoader('Loading support tickets...');
    try {
      const response = await this.supportTicketService.listSupplierTickets({
        supplierId: this.supplierId || undefined,
        limit: 10,
        offset: 0,
        status: statusFilter || undefined
      });
      const rows = Array.isArray((response as any)?.rows)
        ? (response as any).rows
        : Array.isArray(response)
          ? response
          : [];

      this.tickets = rows.map((row: any) => this.mapApiTicket(row));
      this.selectedTicket = this.tickets.length ? this.tickets[0] : null;
      if (this.selectedTicket) {
        this.emailPreview = this.buildEmailPreviewFromTicket(this.selectedTicket, this.newTicket.emails[0]);
      }
      const totalElements = (response as any)?.totalElements ?? rows.length;
      this.updateStatsFromResponse(response, rows, totalElements);
      this.updateSupplierIdentityFromResponse(response);
    } catch (err) {
      console.error('Failed to load supplier support tickets', err);
    } finally {
      this.fullLoaderService.hideLoader();
    }
  }

  private handleFiles(files: FileList | null): void {
    if (!files) {
      return;
    }

    Array.from(files).forEach((file) => {
      this.uploadedFiles.push({
        file,
        name: file.name,
        size: file.size,
        displaySize: this.formatFileSize(file.size)
      });
    });
  }

  private buildEmailPreviewFromTicket(ticket: SupportTicket, fromEmail?: string): EmailPreviewData {
    const email = fromEmail || this.defaultEmail;
    return {
      fromEmail: email,
      subject: `[SUPPORT TICKET ${ticket.id}] ${ticket.subject}`,
      ticketId: ticket.id,
      submitter: `${this.displayName} (${this.accountLabel})`,
      userEmail: email,
      category: ticket.category,
      priority: ticket.priority,
      date: this.getFormattedDate(ticket.created),
      description: ticket.description
    };
  }

  private buildNewTicket(): TicketFormValues {
    return {
      subject: '',
      category: '',
      priority: 'medium',
      description: '',
      emails: [...this.supplierAccountEmails]
    };
  }

  private generateTicketId(): string {
    return 'SUP-' + Math.floor(1000 + Math.random() * 9000);
  }

  private formatPriority(priority: string): SupportTicketPriority {
    switch (priority.toLowerCase()) {
      case 'low':
        return 'Low';
      case 'medium':
        return 'Medium';
      case 'high':
        return 'High';
      case 'urgent':
        return 'Urgent';
      default:
        return 'Medium';
    }
  }

  private getCategoryLabel(value: string): string {
    const categories: Record<string, string> = {
      catalogManagement: 'Catalog Management',
      productManagement: 'Product Management',
      rfqQuoting: 'RFQ & Quoting',
      purchaseOrders: 'Purchase Orders',
      orderDispatch: 'Order Dispatch & Tracking',
      invoiceGeneration: 'Invoice Generation',
      paymentFinance: 'Payment & Finance',
      accountProfileManagement: 'Account & Profile Management',
      technicalIssues: 'Technical Issues',
      other: 'Other'
    };

    return categories[value] || value || 'General';
  }

  private formatStatus(status: string): SupportTicketStatus {
    const normalized = (status || '').toLowerCase();
    switch (normalized) {
      case 'open':
        return 'Open';
      case 'in progress':
      case 'in_progress':
        return 'In Progress';
      case 'resolved':
        return 'Resolved';
      case 'closed':
        return 'Closed';
      default:
        return 'Open';
    }
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  private getFormattedDate(dateValue: string): string {
    const parsed = new Date(dateValue);
    if (isNaN(parsed.getTime())) {
      return new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }

    return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  private parseAttachments(raw: any): { name: string; size: string; icon: string; link?: string }[] {
    if (!raw) return [];

    let parsed: any[] = [];
    try {
      if (typeof raw === 'string') {
        parsed = JSON.parse(raw);
      } else if (Array.isArray(raw)) {
        parsed = raw;
      }
    } catch {
      parsed = [];
    }

    return parsed
      .filter((doc) => !!doc)
      .map((doc) => ({
        name: doc.documentName || doc.name || 'Attachment',
        size: '',
        icon: 'fas fa-file',
        link: doc.documentFileS3Path || doc.s3path || doc.url || ''
      }));
  }

  private mapFilterToStatus(filter: string): string | undefined {
    switch (filter) {
      case 'open':
        return 'Open';
      case 'inProgress':
        return 'In Progress';
      case 'resolved':
        return 'Resolved';
      case 'closed':
        return 'Closed';
      default:
        return undefined;
    }
  }

  private updateStatsFromResponse(response: any, rows: any[], totalElements?: number): void {
    const total = typeof totalElements === 'number' ? totalElements : rows.length;
    const open = response?.openTotalCount ?? undefined;
    const inProgress = response?.inProgressTotalCount ?? undefined;
    const resolved = response?.resolvedTotalCount ?? undefined;
    const closed = response?.closedTotalCount ?? undefined;

    if (
      open !== undefined &&
      inProgress !== undefined &&
      resolved !== undefined &&
      closed !== undefined
    ) {
      this.stats.total = total;
      this.stats.open = open;
      this.stats.inProgress = inProgress;
      this.stats.resolved = resolved;
      return;
    }

    const counts = rows.reduce(
      (acc, row) => {
        const status = (row?.status || '').toLowerCase();
        if (status === 'open') acc.open += 1;
        if (status === 'in progress') acc.inProgress += 1;
        if (status === 'resolved') acc.resolved += 1;
        if (status === 'closed') acc.closed += 1;
        return acc;
      },
      { open: 0, inProgress: 0, resolved: 0, closed: 0 }
    );
    this.stats.total = total;
    this.stats.open = counts.open;
    this.stats.inProgress = counts.inProgress;
    this.stats.resolved = counts.resolved;
  }

  private mapApiTicket(row: any): SupportTicket {
    const createdDate = row?.createdAt ? new Date(row.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
    const priorityLabel = this.formatPriority(row?.priority || 'Medium');
    const attachments = this.parseAttachments(row?.attachments);
    const status = this.formatStatus(row?.status || 'Open');
    const emailsList = (row?.contactEmail || '')
      .split(',')
      .map((e: string) => e.trim())
      .filter(Boolean);
    const supplierDisplayName = row?.supplierCompanyName || row?.supplierName;

    return {
      id: row?.ticketRefNumber || row?.id || this.generateTicketId(),
      ticketRefNumber: row?.ticketRefNumber || row?.id,
      ticketId: row?.id,
      subject: row?.subject || 'Support Ticket',
      category: this.getCategoryLabel(row?.category),
      status: status,
      priority: priorityLabel,
      created: createdDate,
      submitter: `${supplierDisplayName || this.displayName} (${row?.contactEmail || this.defaultEmail}) - ${this.accountLabel}`,
      buyerName: supplierDisplayName,
      emails: emailsList,
      contactEmail: row?.contactEmail,
      description: row?.description || '',
      attachments
    };
  }

  private normalizeEmails(emails: string | string[] | null | undefined): string[] {
    if (!emails) return [];
    if (Array.isArray(emails)) {
      return emails.map((e) => e.trim()).filter(Boolean);
    }
    return emails
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean);
  }

  private updateSupplierIdentityFromResponse(response: any): void {
    const details =
      response?.supplierDetails || response?.supplierDetail || response?.supplierInfo || response?.supplier;
    const firstRow = Array.isArray(response?.rows) && response.rows.length ? response.rows[0] : null;

    const resolvedName =
      details?.supplierCompanyName ||
      details?.companyName ||
      details?.supplierName ||
      details?.name ||
      firstRow?.supplierCompanyName ||
      firstRow?.supplierName ||
      '';

    if (resolvedName) {
      this.supplierAccountName = resolvedName;
      this.supplierName = resolvedName;
    }

    const resolvedEmails = this.normalizeEmails(
      details?.supplierEmail ||
        details?.email ||
        details?.contactEmail ||
        details?.contactEmails ||
        firstRow?.contactEmail
    );

    if (resolvedEmails.length) {
      this.supplierAccountEmails = resolvedEmails;
      if (!this.emailEntry.trim() || !this.newTicket.emails.length) {
        this.newTicket.emails = [...resolvedEmails];
        this.emailEntry = resolvedEmails.join(', ');
      }
    }
  }

  private getStatusUpdateOptions(status: SupportTicketStatus): SupportTicketStatus[] {
    if (status === 'Resolved') return ['Closed'];
    return [];
  }

  private validateStatus(status: string): SupportTicketStatus | null {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'open') return 'Open';
    if (normalized === 'in progress' || normalized === 'in_progress') return 'In Progress';
    if (normalized === 'resolved') return 'Resolved';
    if (normalized === 'closed') return 'Closed';
    return null;
  }

  private async uploadAttachmentsToS3(): Promise<{ documentName: string; documentType: string; s3path: string }[]> {
    const uploads: { documentName: string; documentType: string; s3path: string }[] = [];

    for (const item of this.uploadedFiles) {
      const detail = {
        documentName: item.name,
        documentType: item.file.type || '',
        s3path: ''
      };

      const key = `support-ticket/supplier/${Date.now()}-${Math.random().toString(36).slice(2)}-${item.name}`;
      const s3Params = { fileKey: key, fileContentType: item.file.type || 'application/octet-stream' };

      try {
        const signedUrl = await this.awsSdkService.getSignedUrlPromise(s3Params);
        await this.webApiService.putRequestExternalApi(signedUrl, item.file);
        detail.s3path = this.awsSdkService.extractS3LocationFromUrl(signedUrl);
      } catch (err) {
        console.error('Failed to upload attachment to S3', item.name, err);
      }

      uploads.push(detail);
    }

    return uploads;
  }

  private async triggerAttachmentDownload(attachment: { link?: string; name?: string }): Promise<void> {
    if (!attachment?.link) {
      console.warn(`No link found for attachment: ${attachment?.name || 'Attachment'}`);
      return;
    }

    // console.log('Downloading attachment', attachment?.name, 'from', attachment.link);

    try {
      await new Promise((resolve) => setTimeout(resolve, 50));
      const response = await fetch(attachment.link);
      if (!response.ok) {
        throw new Error(`Failed to fetch attachment: ${response.statusText}`);
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = attachment.name || 'attachment';
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Failed to download attachment', attachment.name, 'link', attachment.link, err);
    }
  }
}
